import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Admin → Businesses (brand level — Catena, Synevo, Hiperdia…).
 *
 * Port of src/app/admin/businesses/{page,businesses-client}.tsx (legacy
 * NextJS) per D21 NO REINVENTION.
 *
 * Branches detail lives at /_authenticated/businesses/$businessId — see
 * `businesses.$businessId.tsx`.
 *
 * Optimizations declared:
 *   - raw fetch / useAdminFetch hook → apiFetch (Architecture Discipline Rule 1)
 *   - useTranslations next-intl → Lingui Trans/t macros (D4)
 *   - useSearchParams + useRouter → TanStack Router validateSearch + useNavigate
 *   - useState/useEffect for fetch → useQuery (shared cache, auto-refetch)
 *
 * Endpoints consumed (apps/api):
 *   GET /api/admin/businesses (list)
 *   GET /api/admin/businesses/summary (countries + categories breakdown)
 */

interface CountryInfo {
  code: string;
  name: Record<string, string>;
  flag: string;
  businesses: number;
}

interface CategoryInfo {
  category: string;
  count: number;
}

interface Business {
  id: string;
  name: Record<string, string>;
  category: string;
  country: string;
  slug: string | null;
  isClaimed: boolean;
  isVerified: boolean;
  planTier: string;
  ratingAvg: number | null;
  reviewCount: number;
  lastScrapedAt: string | null;
  createdAt: string;
  branches: { legalName: string | null; taxId: string | null }[];
  _count: { branches: number };
  pendingClaim: { id: string; createdAt: string } | null;
}

const CATEGORY_META: Record<string, { icon: string; label: string }> = {
  pharmacy: { icon: "💊", label: "Pharmacy" },
  laboratory: { icon: "🔬", label: "Laboratory" },
  imaging: { icon: "📡", label: "Imaging" },
  optical: { icon: "👓", label: "Optical" },
  equipment_supplier: { icon: "⚙️", label: "Equipment" },
  homecare: { icon: "🏥", label: "Home care" },
  medical_transport: { icon: "🚑", label: "Transport" },
};

function displayName(name: Record<string, string> | null, fallback = "—"): string {
  if (!name) return fallback;
  return name.ro || name.en || Object.values(name).find((v) => v && !v.startsWith("_")) || fallback;
}

interface BusinessesSearch {
  country?: string;
  category?: string;
  claimed?: string;
}

const PAGE_SIZE = 100;

export const Route = createFileRoute("/_authenticated/businesses")({
  validateSearch: (search: Record<string, unknown>): BusinessesSearch => ({
    country: typeof search.country === "string" ? search.country : undefined,
    category: typeof search.category === "string" ? search.category : undefined,
    claimed: typeof search.claimed === "string" ? search.claimed : undefined,
  }),
  component: BusinessesPage,
});

function BusinessesPage() {
  const { t } = useLingui();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const [selectedCountry, setSelectedCountry] = useState<string>(search.country ?? "");
  const [selectedCategory, setSelectedCategory] = useState<string>(search.category ?? "");
  const [claimedFilter, setClaimedFilter] = useState<string>(search.claimed ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    void navigate({
      search: () => ({
        country: selectedCountry || undefined,
        category: selectedCategory || undefined,
        claimed: claimedFilter || undefined,
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, selectedCategory, claimedFilter]);

  const summaryQuery = useQuery({
    queryKey: ["admin", "businesses", "summary"],
    queryFn: async (): Promise<{
      countriesWithData: CountryInfo[];
      categories: CategoryInfo[];
    }> => {
      try {
        return await apiFetch.get("/api/admin/businesses/summary");
      } catch (error) {
        if (error instanceof ApiError) return { countriesWithData: [], categories: [] };
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });

  const countries = summaryQuery.data?.countriesWithData ?? [];
  const categories = summaryQuery.data?.categories ?? [];

  const businessesQuery = useQuery({
    queryKey: [
      "admin",
      "businesses",
      "list",
      selectedCountry,
      selectedCategory,
      claimedFilter,
      page,
    ],
    queryFn: async (): Promise<{ businesses: Business[]; total: number }> => {
      try {
        const params = new URLSearchParams();
        if (selectedCountry) params.set("country", selectedCountry);
        if (selectedCategory) params.set("category", selectedCategory);
        if (claimedFilter) params.set("claimed", claimedFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(page * PAGE_SIZE));
        const data = await apiFetch.get<{ businesses: Business[]; total: number }>(
          `/api/admin/businesses?${params.toString()}`,
        );
        return { businesses: data.businesses ?? [], total: data.total ?? 0 };
      } catch (error) {
        if (error instanceof ApiError) return { businesses: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const businesses = businessesQuery.data?.businesses ?? [];
  const total = businessesQuery.data?.total ?? 0;

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return businesses;
    const q = searchQuery.toLowerCase();
    return businesses.filter((b) => {
      if (displayName(b.name).toLowerCase().includes(q)) return true;
      return b.branches.some(
        (br) =>
          (br.legalName || "").toLowerCase().includes(q) ||
          (br.taxId || "").toLowerCase().includes(q),
      );
    });
  }, [searchQuery, businesses]);

  const pendingClaimCount = businesses.filter((b) => b.pendingClaim).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Businesses</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>
            Brand-level records for pharmacy, lab, imaging, optical, equipment, homecare and
            transport services. Branches live under each business.
          </Trans>
        </p>
      </div>

      <section>
        <h2 className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
          <Trans>Countries</Trans>
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCountry("")}
            className={`rounded-full border px-3 py-1 text-sm ${selectedCountry === "" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <Trans>All</Trans>
          </button>
          {countries.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => setSelectedCountry(c.code)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${selectedCountry === c.code ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <span>{c.flag}</span>
              <span>{displayName(c.name, c.code)}</span>
              <span className="text-muted-foreground/70 tabular-nums">({c.businesses})</span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
          <Trans>Categories</Trans>
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory("")}
            className={`rounded-full border px-3 py-1 text-sm ${selectedCategory === "" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            <Trans>All</Trans>
          </button>
          {categories.map((c) => {
            const meta = CATEGORY_META[c.category] || { icon: "📦", label: c.category };
            return (
              <button
                key={c.category}
                type="button"
                onClick={() => setSelectedCategory(c.category)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${selectedCategory === c.category ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                <span>{meta.icon}</span>
                <span>{meta.label}</span>
                <span className="text-muted-foreground/70 tabular-nums">({c.count})</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: "", label: t`All` },
            { key: "true", label: t`Claimed` },
            { key: "false", label: t`Unclaimed` },
            {
              key: "claim_pending",
              label:
                pendingClaimCount > 0 ? t`📨 Claim sent (${pendingClaimCount})` : t`📨 Claim sent`,
            },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setClaimedFilter(opt.key)}
              className={`rounded-full border px-3 py-1 text-xs ${claimedFilter === opt.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Input
          type="search"
          placeholder={t`Search name / legal name / CUI…`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
        <div className="text-muted-foreground ml-auto text-xs tabular-nums">
          <Trans>{total} total</Trans>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Name</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Category</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Country</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Tax ID</Trans>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">
                    <Trans>Branches</Trans>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">
                    <Trans>Claimed</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Actions</Trans>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessesQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                      <Trans>Loading…</Trans>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                      <Trans>No businesses match these filters.</Trans>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((b) => {
                    const meta = CATEGORY_META[b.category] || { icon: "📦", label: b.category };
                    const legalNames = Array.from(
                      new Set(b.branches.map((br) => br.legalName).filter(Boolean) as string[]),
                    );
                    const taxIds = Array.from(
                      new Set(b.branches.map((br) => br.taxId).filter(Boolean) as string[]),
                    );
                    const legalLabel =
                      legalNames.length === 0
                        ? null
                        : legalNames.length === 1
                          ? legalNames[0]
                          : t`${legalNames.length} entities`;
                    const taxLabel =
                      taxIds.length === 0
                        ? "—"
                        : taxIds.length === 1
                          ? taxIds[0]
                          : t`${taxIds.length} CUIs`;
                    return (
                      <TableRow key={b.id}>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{displayName(b.name)}</span>
                            {b.pendingClaim && (
                              <a
                                href={`/claims?open=${b.pendingClaim.id}`}
                                title={t`Claim sent ${new Date(b.pendingClaim.createdAt).toLocaleDateString()} — click for review`}
                                className="inline-flex"
                              >
                                <Badge className="bg-amber-100 text-xs text-amber-800 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300">
                                  📨 <Trans>Claim sent</Trans>
                                </Badge>
                              </a>
                            )}
                          </div>
                          {legalLabel && (
                            <div className="text-muted-foreground text-xs">{legalLabel}</div>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span className="inline-flex items-center gap-1.5">
                            <span>{meta.icon}</span>
                            <span>{meta.label}</span>
                          </span>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{b.country}</TableCell>
                        <TableCell className="hidden tabular-nums lg:table-cell">
                          {taxLabel}
                        </TableCell>
                        <TableCell className="hidden tabular-nums xl:table-cell">
                          {b._count.branches}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {b.isClaimed ? (
                            <Badge variant="default">
                              <Trans>Claimed</Trans>
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <Trans>Unclaimed</Trans>
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Link
                            to="/businesses/$businessId"
                            params={{ businessId: b.id }}
                            className="text-primary text-sm font-medium underline-offset-2 hover:underline"
                          >
                            <Trans>Branches →</Trans>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="border-border rounded border px-3 py-1 disabled:opacity-50"
          >
            <Trans>← Prev</Trans>
          </button>
          <span className="text-muted-foreground tabular-nums">
            <Trans>
              Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
            </Trans>
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total}
            className="border-border rounded border px-3 py-1 disabled:opacity-50"
          >
            <Trans>Next →</Trans>
          </button>
        </div>
      )}
    </div>
  );
}
